import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { investorService, SetPasswordDetailsDto } from '../../../services/investor.service';
import { authenticationService } from '../../../services/authentication.service';
import { useAuth } from '../../../contexts/AuthContext';
import '../IntroducedRegistration/IntroducedInvestorRegistration.scss';
import { CompactHeader } from '../../../components/CompactHeader/CompactHeader';
import { CompactFooter } from '../../../components/CompactFooter/CompactFooter';
import { FiAlertTriangle, FiUser } from 'react-icons/fi';

const UserIcon = FiUser as any;
const AlertIcon = FiAlertTriangle as any;

/** Registration-flow chrome (header, centred card, footer) — same shell as SelfRegistrationSuccess. */
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <CompactHeader />
    <section className="login-form-style4 section-padding py-12 flex justify-center items-center min-h-[60vh]">
      <div className="container flex justify-center">
        <div className="bg-white rounded-xl max-w-sm w-full p-6 shadow-xl border border-slate-100 flex flex-col items-center text-center">
          {children}
        </div>
      </div>
    </section>
    <CompactFooter />
  </>
);

const PRIMARY_BTN = 'w-full py-2 bg-[#3e6f7c] hover:bg-[#355f69] text-white text-[12px] font-bold rounded-md transition-colors cursor-pointer border-0 shadow-sm disabled:opacity-60';
const SECONDARY_BTN = 'w-full py-2 bg-[#f8fafc] hover:bg-slate-100 text-slate-600 text-[12px] font-bold rounded-md transition-colors cursor-pointer border border-slate-200 shadow-sm';

/** Everything loginRedirect needs, computed once from the details call. */
interface HandoffParams {
  scopes: string[];
  authority: string;
  redirectUri: string;
  azureUserId: string;
}

/**
 * FISP-style first-login landing page.
 *
 * <p>Invoked from the registration email link {@code /investor/setpassword/:azureUserId}. Reads
 * the per-tenant B2C config from the backend, picks the reset-password policy when the user has
 * never signed in (or the signin policy for returning users), and triggers
 * {@code msal.loginRedirect()} against that policy. Microsoft B2C then walks the user through
 * password set / login, and the existing {@code /login/callback} handler issues the Facilon JWT.</p>
 *
 * <p>Mirrors FISP {@code SetpasswordComponent.ts}.</p>
 *
 * <p>Shared-browser guard (2026-11-03): if this browser already holds someone's session — a
 * Facilon JWT or a cached MSAL account — the page asks before continuing and signs that person
 * out of this browser first. Until then any cached account was read as "we are back from B2C"
 * and the previous person's token was exchanged: an investor's spouse opening her own
 * set-password link landed on his dashboard, her password never set.</p>
 */
export const InvestorSetPassword: React.FC = () => {
  const { azureUserId } = useParams<{ azureUserId: string }>();
  const navigate = useNavigate();
  const { instance, inProgress } = useMsal();
  const { checkAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<SetPasswordDetailsDto | null>(null);
  // 'detect' = loading + session check; 'confirm' = another person's session found, waiting
  // for the click; 'handoff' = leaving for B2C.
  const [phase, setPhase] = useState<'detect' | 'confirm' | 'handoff'>('detect');
  const [signedInAs, setSignedInAs] = useState<string | null>(null);
  // Guards against React StrictMode double-mount and any other accidental
  // re-entry; MSAL's loginRedirect must not be called twice in flight.
  const startedRef = useRef(false);
  // Set only once we actually hand off to B2C. The cleanup below uses this to decide
  // whether releasing startedRef is safe — see the deadlock note there.
  const handedOffRef = useRef(false);
  // Hand-off parameters survive the confirm pause, so Continue needs no second details call.
  const handoffRef = useRef<HandoffParams | null>(null);

  const handoff = useCallback(async (p: HandoffParams) => {
    // FISP-parity: always redirect through the standard /login/callback (whatever the
    // tenant's redirect_uri says). The B2C reset-password user flow is expected to issue
    // sign-in tokens at the end of its orchestration; LoginCallback then exchanges those
    // tokens for a Facilon JWT and routes to the dashboard. There is no separate /done
    // page — if the user has set a password, they are immediately signed in.

    localStorage.setItem('facilon.setpassword.inProgress', '1');

    // From here we are leaving the app, so the guard must survive any unmount.
    handedOffRef.current = true;

    await instance.loginRedirect({
      scopes: p.scopes,
      authority: p.authority,
      redirectUri: p.redirectUri,
      // Laravel parity (AzureService.php sent prompt=login on every hand-off): B2C must not
      // answer from an SSO session on b2clogin.com, which may belong to the person who was
      // signed in here a moment ago. The reset policy asks for e-mail + code regardless; the
      // sign-in policy (returning users) would otherwise sign that person straight back in.
      prompt: 'login',
      extraQueryParameters: {
        // FISP passes the Azure user id so B2C custom UI can correlate; harmless if unused.
        userId: p.azureUserId,
      },
    });
  }, [instance]);

  useEffect(() => {
    if (!azureUserId) {
      setError('Missing user identifier in link.');
      return;
    }

    // Wait until MSAL has finished consuming any leftover interaction state from a
    // prior visit. Calling loginRedirect while inProgress !== 'none' throws
    // `interaction_in_progress`.
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    let cancelled = false;

    async function go() {
      try {
        // A genuine return from B2C on THIS page — only possible if a tenant's redirect_uri
        // pointed here; Bootstrap processes redirects on /login/callback, so in practice this
        // is null. A cached MSAL account is NOT such a signal: it is whoever used this browser
        // last, and treating it as "auth already completed" was the shared-browser bug.
        const redirectResult = await instance.handleRedirectPromise().catch(() => undefined);
        if (cancelled) return;
        if (redirectResult) {
          navigate('/login/callback', { replace: true });
          return;
        }

        const data = await investorService.getSetPasswordDetails(azureUserId!);
        if (cancelled) return;
        setDetails(data);

        const policy = data.hasLoggedInBefore ? data.signupSigninPolicy : data.resetPasswordPolicy;
        if (!policy) {
          setError(
            data.hasLoggedInBefore
              ? 'Sign-in user flow is not configured for this tenant.'
              : 'Password-reset user flow is not configured for this tenant.'
          );
          return;
        }

        handoffRef.current = {
          scopes: (data.scope || 'openid profile email offline_access').split(/\s+/).filter(Boolean),
          authority: `https://${data.b2cTenantName}.b2clogin.com/${data.b2cTenantName}.onmicrosoft.com/${policy}`,
          redirectUri: data.redirectUri,
          azureUserId: data.azureUserId,
        };

        // Someone is signed in on this browser (app JWT and/or MSAL account). Ask first:
        // continuing signs them out here, and the person at the keyboard may well be them.
        if (authenticationService.isAuthorized() || instance.getAllAccounts().length > 0) {
          setSignedInAs(authenticationService.getUserName());
          setPhase('confirm');
          return;
        }

        setPhase('handoff');
        await handoff(handoffRef.current);
      } catch (e: any) {
        if (cancelled) return;
        // Reset the guard so a manual retry (button click) can fire again if it was a
        // transient state error rather than a config problem.
        startedRef.current = false;
        const msg = e?.response?.data?.message
          || e?.message
          || 'Could not start the password-setup flow.';
        setError(msg);
      }
    }

    go();
    return () => {
      cancelled = true;
      // Release the guard unless we already handed off to B2C.
      //
      // Without this the page deadlocks on "Loading…" whenever the component is
      // remounted before go() finishes — which StrictMode does on EVERY in-app
      // navigation here: mount sets startedRef, the immediate cleanup cancels the
      // in-flight go(), and the remount is then blocked by startedRef, so nothing
      // ever calls setDetails. Opening the URL in a fresh tab hid the bug because
      // MSAL is still starting up there, so the double-invoked mount returns at the
      // inProgress check above without ever setting the guard.
      //
      // Safe to release: go() re-checks `cancelled` after every await, so the
      // cancelled run cannot reach loginRedirect once a newer run has taken over.
      if (!handedOffRef.current) {
        startedRef.current = false;
      }
    };
  }, [azureUserId, instance, inProgress, handoff]);

  /**
   * Confirm step: forget whoever this browser was, then hand off exactly as the no-session
   * path does. The previous person's other tabs lose their token too (shared localStorage)
   * and fall back to /login on their next request — intended: one browser, one person.
   */
  const continueAsNewUser = async () => {
    const params = handoffRef.current;
    if (!params) return;
    setPhase('handoff');
    try {
      authenticationService.purgeIdentityStorage();
      try { await instance.clearCache(); } catch { /* nothing cached on the MSAL side */ }
      // AuthContext still says "signed in" from its mount-time read; re-read so a failure
      // below leaves the app knowing the browser is signed out.
      checkAuth();
      await handoff(params);
    } catch (e: any) {
      startedRef.current = false;
      setError(e?.message || 'Could not start the password-setup flow.');
    }
  };

  if (error) {
    const isInteractionInProgress = error.toLowerCase().includes('interaction_in_progress');
    return (
      <Shell>
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <AlertIcon size={30} className="text-red-600 flex-shrink-0" />
        </div>
        <h3 className="text-[22px] font-bold text-slate-800 mb-2">We couldn't open your setup link</h3>
        <div className="text-[12px] text-slate-500 mb-4 max-w-[280px] leading-relaxed">
          <p className="mb-2">{error}</p>
          {isInteractionInProgress && (
            <p className="mb-0">
              A previous sign-in attempt is still being processed. Retry clears it and starts again.
            </p>
          )}
        </div>
        <div className="w-full flex flex-col gap-2">
          <button
            type="button"
            className={PRIMARY_BTN}
            onClick={() => {
              // Clear any MSAL state left from an interrupted prior flow, then retry.
              try {
                Object.keys(localStorage)
                  .filter((k) => k.startsWith('msal.') || k.includes('interaction.status') || k === 'facilon.setpassword.inProgress')
                  .forEach((k) => localStorage.removeItem(k));
              } catch {}
              window.location.reload();
            }}
          >
            Retry
          </button>
          <button type="button" className={SECONDARY_BTN} onClick={() => navigate('/login')}>
            Go to Login
          </button>
        </div>
      </Shell>
    );
  }

  if (phase === 'confirm' && details) {
    const who = signedInAs || 'another user';
    return (
      <Shell>
        <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mb-4">
          <UserIcon size={30} className="text-amber-600 flex-shrink-0" />
        </div>
        <h3 className="text-[22px] font-bold text-slate-800 mb-2">This browser is already signed in</h3>
        <div className="text-[12px] text-slate-500 mb-4 max-w-[280px] leading-relaxed">
          <p className="mb-2">
            This browser is signed in as <strong className="text-slate-700">{who}</strong>. Setting a password for{' '}
            <strong className="text-slate-700">{details.displayName}</strong> will sign {who} out of Facilon on this browser.
          </p>
          <p className="mb-0">
            If you are {details.displayName}, continue. Otherwise cancel and open this link in a
            private window or on your own device.
          </p>
        </div>
        <div className="w-full flex flex-col gap-2">
          <button type="button" className={PRIMARY_BTN} onClick={continueAsNewUser}>
            Continue — sign out and set my password
          </button>
          <button type="button" className={SECONDARY_BTN} onClick={() => navigate('/login')}>
            Cancel — stay signed in as {who}
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="w-16 h-16 bg-[#3e6f7c]/10 rounded-full flex items-center justify-center mb-4">
        <div className="spinner-border text-[#3e6f7c]" role="status" style={{ width: 28, height: 28, borderWidth: 3 }}>
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
      <h3 className="text-[22px] font-bold text-slate-800 mb-2">
        {details && phase === 'handoff' ? `Welcome ${details.displayName}` : 'One moment…'}
      </h3>
      <p className="text-[12px] text-slate-500 mb-0 max-w-[280px] leading-relaxed">
        {details && phase === 'handoff'
          ? 'Taking you to our secure page to set your password…'
          : 'Opening your setup link…'}
      </p>
    </Shell>
  );
};

export default InvestorSetPassword;
